import { Color } from "@repo/app-types/constants/colors";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import type { FC } from "react";

/**
 * Narrowed 1-5 customer-experience score. The broader
 * `customer_experience.score` on the API is `number | null`; this UI
 * type is the non-null integer variant call sites narrow into before
 * passing to the pill.
 */
export type CustomerExperienceScoreValue = 1 | 2 | 3 | 4 | 5;

export type CustomerExperienceScoreBand = "low" | "neutral" | "high";

/** Display label for each 1-5 customer-experience score, ordered to read 1 → 5. */
export const CUSTOMER_EXPERIENCE_SCORE_LABEL: Record<
  CustomerExperienceScoreValue,
  string
> = {
  1: "Damaging",
  2: "Poor",
  3: "Neutral",
  4: "Good",
  5: "Exceptional",
};

/** Maps a 1-5 score onto its coarse low/neutral/high band. */
export function customerExperienceScoreToBand(
  score: CustomerExperienceScoreValue,
): CustomerExperienceScoreBand {
  if (score <= 2) {
    return "low";
  }
  if (score === 3) {
    return "neutral";
  }
  return "high";
}

const BAND_PILL_COLOR: Record<CustomerExperienceScoreBand, PillColor> = {
  low: Color.Red,
  neutral: Color.Gray,
  high: Color.Green,
};

interface GradingCustomerExperienceScorePillProps {
  score: CustomerExperienceScoreValue | null;
  size?: Size;
}

/**
 * Renders a single grading's customer-experience score as
 * `{score} · {label}` (e.g. "4 · Good"), color-coded by band: low
 * (1-2) red, neutral (3) gray, high (4-5) green. Falls back to a
 * muted "—" pill when the call has no customer-experience read
 * (voicemail, no customer voice, unscored grading). The aggregate
 * variant used on the team page lives in
 * `customerExperienceScorePercentPill.tsx`.
 */
export const GradingCustomerExperienceScorePill: FC<
  GradingCustomerExperienceScorePillProps
> = ({ score, size = Size.SMALL }) => {
  if (score === null) {
    return <Pill color={Color.Gray} size={size} label="—" />;
  }
  return (
    <Pill
      size={size}
      color={BAND_PILL_COLOR[customerExperienceScoreToBand(score)]}
      label={`${score} · ${CUSTOMER_EXPERIENCE_SCORE_LABEL[score]}`}
    />
  );
};
